// B6 — the gene-symbol autocomplete: canonical symbols offered from the registry, each showing HOW
// it matched (alias / previous symbol). Selecting an option is a VISIBLE, author-chosen rewrite to
// the canonical symbol — typed text is never transformed on save. Free text stays legal: 17
// unresolved symbols are real content, so unresolved is a warning, never a block.
import React, { useEffect, useRef, useState } from "react";
import { AutoComplete } from "antd";
import { CheckOutlined, LockOutlined, WarningOutlined } from "@ant-design/icons";

import { searchRegistryGenes } from "../../../../api/geneCodes/RegistryApi";
import { RegistryGene, RegistrySearchOutcome } from "../../../../types/geneCodes/RegistryTypes";
import { SymbolResolution, resolveSymbol, symbolMatchFor } from "../../../../utils/geneCodes/registryResolution";
import "./wheelBuilder.css";

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;
const LIMIT = 10;

interface GeneSymbolFieldProps {
	// Injected by Form.Item; `id` must reach the inner input or the label loses its control.
	value?: string;
	onChange?: (value: string) => void;
	id?: string;
	placeholder?: string;
	/** Fires only on an option pick — the moment a canonical symbol (and its registry id) is chosen. */
	onPickGene?: (gene: RegistryGene) => void;
	testId?: string;
}

const GeneSymbolField: React.FC<GeneSymbolFieldProps> = ({ value, onChange, id, placeholder, onPickGene, testId }) => {
	const text = typeof value === "string" ? value : "";
	const [outcome, setOutcome] = useState<RegistrySearchOutcome<RegistryGene> | null>(null);
	const [searchedFor, setSearchedFor] = useState("");
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const query = text.trim();
		if (query.length < MIN_QUERY) {
			setOutcome(null);
			setSearchedFor("");
			return undefined;
		}

		let live = true;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			void searchRegistryGenes(query, LIMIT).then((result) => {
				if (!live) return;
				setOutcome(result);
				setSearchedFor(query);
			});
		}, DEBOUNCE_MS);

		return () => {
			live = false;
			if (timer.current) clearTimeout(timer.current);
		};
	}, [text]);

	const results = outcome?.status === "ok" ? outcome.results : [];

	const options = results.map((gene) => {
		const match = symbolMatchFor(searchedFor, gene);
		return {
			value: gene.symbol,
			label: (
				<span className="mg-gene-field__option" data-testid="gene-symbol-option" data-symbol={gene.symbol}>
					<span className="mg-gene-field__option-symbol">{gene.symbol}</span>
					<span className="mg-gene-field__option-name">{gene.name ?? ""}</span>
					{match?.via ? (
						<span className="mg-gene-field__option-via" data-testid="gene-symbol-option-via">
							via {match.via}
						</span>
					) : null}
				</span>
			),
		};
	});

	// The status only speaks about text the registry has actually answered for — never mid-debounce.
	const resolution: SymbolResolution | null = outcome && searchedFor !== "" && searchedFor === text.trim() ? resolveSymbol(searchedFor, outcome) : null;

	const handleSelect = (selected: string) => {
		onChange?.(selected);
		if (!onPickGene) return;
		const picked = results.find((gene) => gene.symbol === selected);
		if (picked) onPickGene(picked);
	};

	return (
		<div className="mg-gene-field" data-testid={testId ?? "gene-symbol-field"}>
			<AutoComplete value={text} onChange={(next) => onChange?.(next ?? "")} onSelect={handleSelect} options={options} id={id} placeholder={placeholder} filterOption={false} />
			{resolution ? (
				<span className={`mg-gene-field__status mg-gene-field__status--${resolution.state}`} data-testid="gene-symbol-status" data-state={resolution.state}>
					{resolution.state === "resolved" ? (
						<>
							<CheckOutlined /> In the registry · {resolution.gene.id}
						</>
					) : resolution.state === "alias" ? (
						<>
							<WarningOutlined /> {searchedFor} matches {resolution.candidates[0].gene.symbol} via {resolution.candidates[0].via} — pick it above to use the current symbol.
						</>
					) : resolution.state === "unresolved" ? (
						<>
							<WarningOutlined /> “{searchedFor}” is not in the registry — it will be saved as typed.
						</>
					) : (
						<>
							<LockOutlined /> Registry unavailable — “{searchedFor}” could not be checked.
						</>
					)}
				</span>
			) : null}
		</div>
	);
};

export default GeneSymbolField;
